"use client";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Search, Plus, Edit3, Trash2, Grid3X3, Tag, Table, Layers,
  CheckCircle, XCircle, LayoutList, RefreshCcw, X, ChevronDown,
  Package,
} from 'lucide-react';
import ModalModal from './ModalModal';
import Pagination, { usePagination } from '../shared/Pagination';
import { useModels } from '../context/ModelContext';
import { useCategories } from '../context/CategoryContext';
import { useSubCategories } from '../context/SubcategoryContext';

const truncateChars = (str, len = 14) => {
  if (!str) return '';
  return str.length <= len ? str : str.slice(0, len) + '…';
};

const ModalPage = () => {
  const { models, setModels, toggleStatus, updateModel, addModel, deleteModel } = useModels();
  const { categories, fetchCategories } = useCategories();
  const { subCategories, fetchSubCategories } = useSubCategories();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subCategoryFilter, setSubCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingModal, setEditingModal] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
  }, []);

  useEffect(() => {
    fetchCategories().catch(e => console.warn('Failed to fetch categories', e));
    fetchSubCategories().catch(e => console.warn('Failed to fetch sub-categories', e));
  }, [fetchCategories, fetchSubCategories]);

  // ── Effective status (respects parent inactive state) ──────────────────────
  const getEffectiveStatus = useCallback((modal) => {
    const cat = categories.find(c => String(c.id) === String(modal.category_id));
    const sub = subCategories.find(s => String(s.id) === String(modal.sub_category_id));
    return cat?.status === 'Inactive' || sub?.status === 'Inactive' ? 'Inactive' : modal.status;
  }, [categories, subCategories]);

  const isParentInactive = useCallback((modal) => {
    const cat = categories.find(c => String(c.id) === String(modal.category_id));
    const sub = subCategories.find(s => String(s.id) === String(modal.sub_category_id));
    if (cat?.status === 'Inactive') return 'category';
    if (sub?.status === 'Inactive') return 'subcategory';
    return null;
  }, [categories, subCategories]);

  // ── KPIs ────────────────────────────────────────────────────────────────────
  const totalCount    = models.length;
  const activeCount   = models.filter(m => getEffectiveStatus(m) === 'Active').length;
  const inactiveCount = totalCount - activeCount;

  // ── Filter options ──────────────────────────────────────────────────────────
  const categoryOptions = useMemo(() => {
    const opts = categories
      .map(c => ({
        value: c.id,
        label: c.name,
        count: models.filter(m => String(m.category_id) === String(c.id)).length,
      }))
      .filter(o => o.count > 0);
    return [{ value: 'all', label: 'All Categories', count: totalCount }, ...opts];
  }, [categories, models, totalCount]);

  const subCategoryOptions = useMemo(() => {
    const base = categoryFilter === 'all' ? models : models.filter(m => String(m.category_id) === String(categoryFilter));
    const relevantSubs = categoryFilter === 'all'
      ? subCategories
      : subCategories.filter(s => String(s.category_id) === String(categoryFilter));
    const opts = relevantSubs
      .map(s => ({
        value: s.id,
        label: s.subcat_name,
        count: base.filter(m => String(m.sub_category_id) === String(s.id)).length,
      }))
      .filter(o => o.count > 0);
    return [{ value: 'all', label: 'All Sub-Categories', count: base.length }, ...opts];
  }, [subCategories, models, categoryFilter]);

  // Auto-reset dependent filters
  useEffect(() => {
    if (categoryFilter !== 'all' && !categoryOptions.some(o => String(o.value) === String(categoryFilter))) {
      setCategoryFilter('all');
      setSubCategoryFilter('all');
    }
  }, [categoryOptions, categoryFilter]);

  useEffect(() => {
    if (subCategoryFilter !== 'all' && !subCategoryOptions.some(o => String(o.value) === String(subCategoryFilter))) {
      setSubCategoryFilter('all');
    }
  }, [subCategoryOptions, subCategoryFilter]);

  // ── Filtered + sorted list ─────────────────────────────────────────────────
  const filteredModels = useMemo(() => {
    const parseDate = v => { if (!v) return 0; const t = new Date(v.replace(' ', 'T')).getTime(); return isNaN(t) ? 0 : t; };
    return models
      .filter(m => {
        const q = searchTerm.toLowerCase();
        return (
          (!q || m.model_name?.toLowerCase().includes(q) || m.sub_category_name?.toLowerCase().includes(q) || m.category_name?.toLowerCase().includes(q)) &&
          (statusFilter === 'all' || getEffectiveStatus(m) === statusFilter) &&
          (categoryFilter === 'all' || String(m.category_id) === String(categoryFilter)) &&
          (subCategoryFilter === 'all' || String(m.sub_category_id) === String(subCategoryFilter))
        );
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':        return (a.model_name || '').localeCompare(b.model_name || '');
          case 'name-desc':   return (b.model_name || '').localeCompare(a.model_name || '');
          case 'category':    return (a.category_name || '').localeCompare(b.category_name || '');
          case 'subcategory': return (a.sub_category_name || '').localeCompare(b.sub_category_name || '');
          case 'newest':      return parseDate(b.create_audit_time) - parseDate(a.create_audit_time);
          case 'oldest':      return parseDate(a.create_audit_time) - parseDate(b.create_audit_time);
          default:            return 0;
        }
      });
  }, [models, searchTerm, statusFilter, categoryFilter, subCategoryFilter, sortBy, getEffectiveStatus]);

  const { page, pageSize, setPage, setPageSize, totalPages, totalItems, paginated: paginatedModels }
    = usePagination(filteredModels, 10);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await toggleStatus(id, currentStatus);
      showToast(`Status updated to ${currentStatus === 'Active' ? 'Inactive' : 'Active'} successfully!`);
    } catch { showToast('Failed to update status.', 'error'); }
  };

  const handleDeleteModal = async id => {
    if (!window.confirm('Are you sure you want to delete this model?')) return;
    try { await deleteModel(id); } catch (e) { console.error('Failed to delete model:', e); }
  };

  const handleEditModal  = modal => { setEditingModal(modal); setShowAddModal(true); };
  const handleAddModal   = ()    => { setEditingModal(null);  setShowAddModal(true); };

  const enrichModel = (raw, data) => {
    const cat = categories.find(c => String(c.id) === String(raw?.category_id ?? data.categoryId));
    const sub = subCategories.find(s => String(s.id) === String(raw?.sub_category_id ?? data.subCategoryId));
    return {
      ...raw,
      category_name: cat?.name || data.categoryName || '',
      sub_category_name: sub?.subcat_name || data.subCategoryName || '',
    };
  };

  const handleSaveModal = async modalData => {
    try {
      if (editingModal) {
        const updated = await updateModel(editingModal.id, modalData);
        if (!updated?.category_name || !updated?.sub_category_name) {
          setModels(prev => prev.map(m => m.id === editingModal.id ? enrichModel(updated, modalData) : m));
        }
      } else {
        await addModel(modalData);
      }
      setEditingModal(null);
      showToast(editingModal ? 'Model updated successfully!' : 'Model created successfully!');
    } catch { showToast('Failed to save model.', 'error'); }
  };

  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setCategoryFilter('all'); setSubCategoryFilter('all'); setSortBy('name'); };
  const hasFilters = searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || subCategoryFilter !== 'all' || sortBy !== 'name';

  // ── Sub-components ─────────────────────────────────────────────────────────
  const ToggleSwitch = ({ modalId, status, disabled = false }) => (
    <label
      className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      title={disabled ? 'Parent is inactive' : status === 'Active' ? 'Set Inactive' : 'Set Active'}
    >
      <input
        type="checkbox"
        checked={status === 'Active'}
        onChange={() => !disabled && handleToggleStatus(modalId, status)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors duration-300 shadow-sm" />
      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5" />
    </label>
  );

  const ModelImage = ({ modal, className }) =>
    modal.image ? (
      <img src={modal.image} alt={modal.model_name} className={`object-cover border border-gray-100 ${className}`}
        onError={e => { e.target.src = 'https://placehold.co/400x180?text=No+Image'; }} />
    ) : (
      <div className={`bg-gray-100 flex items-center justify-center ${className}`}>
        <Layers className="w-5 h-5 text-gray-400" />
      </div>
    );

  // ── Grid Card (mirrors SubCategoryCard layout exactly) ─────────────────────
  const ModelCard = ({ modal }) => {
    const parentInactive = isParentInactive(modal);
    const isGrayed = !!parentInactive || modal.status === 'Inactive';
    const [expanded, setExpanded] = useState(false);
    const desc = modal.description || '';
    const isLong = desc.length > 80;
    const modelNameShort = truncateChars(modal.model_name, 28);
    const categoryNameShort = truncateChars(modal.category_name, 14);
    const subCategoryNameShort = truncateChars(modal.sub_category_name, 14);

    return (
      <div className={`bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex flex-col transition-all duration-300 group
        ${isGrayed ? 'opacity-55 grayscale' : 'hover:shadow-lg hover:shadow-teal-100/60 hover:-translate-y-0.5'}`}>

        {/* Image */}
        <div className="relative w-full h-44 overflow-hidden bg-gray-100 flex-shrink-0">
          {modal.image ? (
            <img src={modal.image} alt={modal.model_name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={e => { e.target.src = 'https://placehold.co/400x180?text=No+Image'; }} />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <Layers className="w-12 h-12 text-gray-300" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">
            <h3 className="text-white font-bold text-[15px] leading-snug drop-shadow truncate whitespace-nowrap overflow-hidden" title={modal.model_name}>
              {modelNameShort}
            </h3>
          </div>

          {parentInactive && (
            <div className="absolute top-3 left-3">
              <span className="px-2 py-1 bg-gray-800/80 text-white text-[10px] font-semibold rounded-full backdrop-blur-sm">
                {parentInactive === 'category' ? 'Category Inactive' : 'Sub-Category Inactive'}
              </span>
            </div>
          )}

          <div className="absolute top-3 right-3">
            <ToggleSwitch modalId={modal.id} status={modal.status} disabled={!!parentInactive} />
          </div>
        </div>

        {/* Body */}
        <div className="p-4 flex flex-col gap-2.5 flex-1">
          {/* Breadcrumb category → sub-category */}
          <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden min-w-0">
            <Tag className="w-3 h-3 text-teal-500 flex-shrink-0" />
            <span className="text-xs text-teal-600 font-medium truncate whitespace-nowrap overflow-hidden shrink" title={modal.category_name || '—'}>
              {categoryNameShort || '—'}
            </span>
            <span className="text-xs text-gray-400 flex-shrink-0">→</span>
            <span className="text-xs text-teal-400 truncate whitespace-nowrap overflow-hidden shrink min-w-0" title={modal.sub_category_name || '—'}>
              {subCategoryNameShort || '—'}
            </span>
          </div>

          {/* Description */}
          <div className="flex-1">
            {desc ? (
              <>
                <p className={`text-xs text-gray-500 leading-relaxed break-words ${expanded ? '' : 'line-clamp-2'}`}>{desc}</p>
                {isLong && (
                  <button onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
                    className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 mt-0.5 transition-colors">
                    {expanded ? 'Show less ↑' : 'Read more ↓'}
                  </button>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-400 italic">No description provided.</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2.5 border-t border-gray-100 mt-auto">
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
              getEffectiveStatus(modal) === 'Active'
                ? 'text-green-700 bg-green-50'
                : 'text-red-500 bg-red-50'
            }`}>
              {getEffectiveStatus(modal) === 'Active'
                ? <CheckCircle className="w-3 h-3" />
                : <XCircle className="w-3 h-3" />}
              {getEffectiveStatus(modal)}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => handleEditModal(modal)}
                className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDeleteModal(modal.id)}
                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── List row (mirrors SubCategoryPage ListRow) ─────────────────────────────
  const ListRow = ({ modal, idx }) => {
    const parentInactive = isParentInactive(modal);
    const isGrayed = !!parentInactive || modal.status === 'Inactive';
    const categoryNameShort = truncateChars(modal.category_name, 16);
    const subCategoryNameShort = truncateChars(modal.sub_category_name, 16);
    return (
      <div className={`flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-4 py-3 transition-all duration-200
        ${isGrayed ? 'opacity-55 grayscale' : 'hover:shadow-sm hover:border-teal-200'}`}>
        <span className="text-xs text-gray-400 w-6 text-center flex-shrink-0">{idx + 1}</span>
        <ModelImage modal={modal} className="w-11 h-11 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{modal.model_name}</p>
          <div className="flex items-center gap-1 mt-0.5 flex-nowrap overflow-hidden min-w-0">
            <Tag className="w-3 h-3 text-teal-500 flex-shrink-0" />
            <span className="text-xs text-teal-600 truncate whitespace-nowrap overflow-hidden shrink" title={modal.category_name || '—'}>
              {categoryNameShort || '—'}
            </span>
            <span className="text-xs text-gray-400 flex-shrink-0">→</span>
            <span className="text-xs text-gray-500 truncate whitespace-nowrap overflow-hidden shrink min-w-0" title={modal.sub_category_name || '—'}>
              {subCategoryNameShort || '—'}
            </span>
          </div>
        </div>
        <span className={`hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
          getEffectiveStatus(modal) === 'Active' ? 'text-green-700 bg-green-50' : 'text-red-500 bg-red-50'
        }`}>
          {getEffectiveStatus(modal)}
        </span>
        <ToggleSwitch modalId={modal.id} status={modal.status} disabled={!!parentInactive} />
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => handleEditModal(modal)}
            className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => handleDeleteModal(modal.id)}
            className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  const isEmpty = filteredModels.length === 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Models',  value: totalCount,    icon: LayoutList,  accent: 'border-l-teal-500',  color: 'text-teal-700',  bg: 'bg-teal-50'  },
            { label: 'Active',        value: activeCount,   icon: CheckCircle, accent: 'border-l-green-500', color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Inactive',      value: inactiveCount, icon: XCircle,     accent: 'border-l-red-400',   color: 'text-red-500',   bg: 'bg-red-50'   },
          ].map(({ label, value, icon: Icon, accent, color, bg }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
              <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 leading-tight">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Search & Filter bar ── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3 flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search models, categories…"
              className="w-full pl-9 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category filter */}
          <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setSubCategoryFilter('all'); }}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[150px]">
            {categoryOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label} ({o.count})</option>
            ))}
          </select>

          {/* Sub-Category filter */}
          <select value={subCategoryFilter} onChange={e => setSubCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[170px]">
            {subCategoryOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label} ({o.count})</option>
            ))}
          </select>

          {/* Status filter */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[130px]">
            <option value="all">All Status ({totalCount})</option>
            <option value="Active">Active ({activeCount})</option>
            <option value="Inactive">Inactive ({inactiveCount})</option>
          </select>

          {/* Sort */}
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[140px]">
            <option value="name">Name A → Z</option>
            <option value="name-desc">Name Z → A</option>
            <option value="category">By Category</option>
            <option value="subcategory">By Sub-Category</option>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>

          {/* Clear */}
          {hasFilters && (
            <button onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg hover:border-red-200 transition-colors">
              <RefreshCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}

          <div className="hidden sm:block w-px h-6 bg-gray-200 flex-shrink-0" />

          {/* View toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
            {[{ mode: 'grid', Icon: Grid3X3 }, { mode: 'list', Icon: Layers }, { mode: 'table', Icon: Table }].map(({ mode, Icon }) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                className={`p-1.5 rounded-md transition-colors ${viewMode === mode ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                title={mode.charAt(0).toUpperCase() + mode.slice(1) + ' view'}>
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>

          {/* Add button */}
          <button onClick={handleAddModal}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Model</span>
          </button>
        </div>

        {/* ── Results count ── */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Showing <span className="font-semibold text-gray-700">{filteredModels.length}</span> of{' '}
            <span className="font-semibold text-gray-700">{totalCount}</span> models
          </span>
        </div>

        {/* ── Content ── */}
        {isEmpty ? (
          <div className="bg-white border border-gray-200 rounded-2xl py-20 text-center shadow-sm">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Layers className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-500 mb-1">No models found</h3>
            <p className="text-sm text-gray-400 mb-5">
              {hasFilters ? 'Try adjusting your search or filters.' : 'Create your first model to get started.'}
            </p>
            {hasFilters ? (
              <button onClick={clearFilters}
                className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                Clear filters
              </button>
            ) : (
              <button onClick={handleAddModal}
                className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors">
                Add Model
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Grid */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {paginatedModels.map(m => <ModelCard key={m.id} modal={m} />)}
              </div>
            )}

            {/* List */}
          {/* List */}
{viewMode === 'list' && (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
    <div className="space-y-2 p-3">
      {/* Header */}
      <div className="flex items-center gap-4 px-4 py-3 bg-teal-700 text-white text-xs font-semibold uppercase tracking-wider rounded-xl">
        <span className="w-6 text-teal-200">ID</span>
        <span className="w-11">Image</span>
        <span className="flex-1">Name / Category / Sub-Category</span>
        <span className="hidden sm:block w-20 text-center">Status</span>
        <span className="w-12 text-center">Toggle</span>
        <span className="w-16 text-right">Actions</span>
      </div>
      {paginatedModels.map((m, i) => <ListRow key={m.id} modal={m} idx={i} />)}
    </div>
    <div className="px-4 border-t border-gray-100">
      <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
        pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
    </div>
  </div>
)}
            {/* Table */}
            {viewMode === 'table' && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead>
                      <tr className="bg-teal-700 text-white">
                        {['ID', 'Image', 'Model', 'Category', 'Sub-Category', 'Status', 'Actions'].map((h, i) => (
                          <th key={h} className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider ${
                            i === 6 ? 'text-right' : i === 0 ? 'text-center w-10' : 'text-left'
                          } ${h === 'Sub-Category' ? 'hidden sm:table-cell' : ''}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedModels.map((m, i) => {
                        const parentInactive = isParentInactive(m);
                        return (
                          <tr key={m.id} className={`transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} ${parentInactive ? 'opacity-55 grayscale' : 'hover:bg-teal-50/40'}`}>
                            <td className="px-4 py-3 text-xs text-gray-400 text-center">{i + 1}</td>
                            <td className="px-4 py-3">
                              <ModelImage modal={m} className="w-10 h-10 rounded-xl" />
                            </td>
                            <td className="px-4 py-3 font-semibold text-gray-800 max-w-[140px] truncate" title={m.model_name}>{truncateChars(m.model_name, 18)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 flex-nowrap overflow-hidden">
                                <Tag className="w-3 h-3 text-teal-500 flex-shrink-0" />
                                <span className="text-gray-600 text-xs truncate whitespace-nowrap overflow-hidden max-w-[100px]" title={m.category_name || '—'}>
                                  {truncateChars(m.category_name, 14) || '—'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600 text-xs hidden sm:table-cell truncate whitespace-nowrap overflow-hidden max-w-[120px]" title={m.sub_category_name || '—'}>
                              {truncateChars(m.sub_category_name, 16) || '—'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <ToggleSwitch modalId={m.id} status={m.status} disabled={!!parentInactive} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => handleEditModal(m)}
                                  className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Edit">
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleDeleteModal(m.id)}
                                  className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 border-t border-gray-100">
                  <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
                    pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
                </div>
              </div>
            )}

            {/* Pagination for grid + list */}
            {viewMode !== 'table' && totalPages > 1 && (
              <div className="bg-white border border-gray-200 rounded-xl px-4 shadow-sm">
                <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
                  pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Drawer */}
      <ModalModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingModal(null); }}
        modal={editingModal}
        categories={categories}
        models={models} 
        subCategories={subCategories}
        onSave={handleSaveModal}
      />

      {/* Toast */}
      <div style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        transition: 'all 0.35s ease',
        opacity: toast.visible ? 1 : 0,
        transform: toast.visible ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.96)',
        pointerEvents: toast.visible ? 'auto' : 'none',
      }}>
        <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-semibold ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-teal-600 text-white'
        }`}>
          {toast.type === 'error'
            ? <XCircle className="w-4 h-4 flex-shrink-0" />
            : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
          {toast.message}
        </div>
      </div>
    </div>
  );
};

export default ModalPage;